import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Container for the entire page
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background.main};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
  }
`;

// Left side with logo
const LogoSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 30%;
  background-color: #1A1625;
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
    padding-top: 60px;
    padding-bottom: 30px;
  }
`;

// Logo container
const Logo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    height: 60px;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

// Content side
const ContentSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 70%;
  padding: ${({ theme }) => theme.spacing.xxl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.md};
  }
`;

// Profile card
const ProfileCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 100%;
  }
`;

// Title
const Title = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 28px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    text-align: left;
  }
`;

// Subtitle
const Subtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    text-align: left;
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
`;

// Form
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

// Form group
const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

// Label
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Label = styled.label`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

// Input
const Input = styled.input`
  background-color: ${({ theme }) => theme.colors.background.main};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  width: 100%;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

// Text area
const TextArea = styled.textarea`
  background-color: ${({ theme }) => theme.colors.background.main};
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 16px;
  width: 100%;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

// Profile picture container
const ProfilePictureContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  position: relative;
`;

// Profile picture
const ProfilePicture = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  background-color: #333;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// Camera icon button
const CameraButton = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  background-color: #9333EA;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

// Cover image upload area
const CoverUploadArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.xl};
  cursor: pointer;
  transition: border-color 0.2s;
  background-color: #1A1625;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  height: 120px;
  
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

// Upload icon
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UploadIcon = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 24px;
`;

// Upload text
const UploadText = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  text-align: center;
`;

// Setup profile button
const SetupButton = styled.button`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
  border-radius: 4px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: #9333EA;
  color: white;
  border: none;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.action.hover};
  }
  
  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    cursor: not-allowed;
  }
`;

const CompleteProfile: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const profileInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    username: '@chrissburkhard',
    firstName: '',
    lastName: '',
    tagline: '',
    bio: '',
    profilePicture: null as File | null,
    coverImage: null as File | null,
    profilePreview: '',
    coverPreview: ''
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleProfilePictureClick = () => {
    if (profileInputRef.current) {
      profileInputRef.current.click();
    }
  };
  
  const handleCoverImageClick = () => {
    if (coverInputRef.current) {
      coverInputRef.current.click();
    }
  };
  
  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ 
        ...prev, 
        profilePicture: file,
        profilePreview: URL.createObjectURL(file)
      }));
    }
  };
  
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({ 
        ...prev, 
        coverImage: file,
        coverPreview: URL.createObjectURL(file)
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      navigate('/enterprises');
    }, 1500);
  };
  
  return (
    <Container>
      <LogoSide>
        <Logo>
          <img src="/creator-logo.svg" alt="BlinkLink Creator" />
        </Logo>
      </LogoSide>
      
      <ContentSide>
        <ProfileCard>
          <Title>Complete Creator Profile</Title>
          <Subtitle>Your profile that faces Enterprises and other creators</Subtitle>
          
          <Form onSubmit={handleSubmit}>
            {/* Profile Picture */}
            <ProfilePictureContainer>
              <ProfilePicture onClick={handleProfilePictureClick}>
                {formData.profilePreview ? (
                  <img src={formData.profilePreview} alt="Profile" />
                ) : null}
              </ProfilePicture>
              <CameraButton onClick={handleProfilePictureClick}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 12.5C9.93 12.5 11.5 10.93 11.5 9C11.5 7.07 9.93 5.5 8 5.5C6.07 5.5 4.5 7.07 4.5 9C4.5 10.93 6.07 12.5 8 12.5Z" fill="white"/>
                  <path d="M2 5.5C2 4.4 2.9 3.5 4 3.5H5.5L6.5 2H9.5L10.5 3.5H12C13.1 3.5 14 4.4 14 5.5V12.5C14 13.6 13.1 14.5 12 14.5H4C2.9 14.5 2 13.6 2 12.5V5.5Z" stroke="white" strokeWidth="1.5"/>
                </svg>
              </CameraButton>
              <input 
                type="file" 
                ref={profileInputRef}
                accept="image/*" 
                onChange={handleProfilePictureChange} 
                style={{ display: 'none' }} 
              />
            </ProfilePictureContainer>
            
            {/* Cover Image */}
            <CoverUploadArea onClick={handleCoverImageClick}>
              {formData.coverPreview ? (
                <img src={formData.coverPreview} alt="Cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM13.96 12.29L11.21 15.83L9.25 13.47L6.5 17H17.5L13.96 12.29Z" fill="#9333EA"/>
                  </svg>
                  <UploadText>Tap here to upload cover image</UploadText>
                </>
              )}
              <input 
                type="file" 
                ref={coverInputRef}
                accept="image/*" 
                onChange={handleCoverImageChange} 
                style={{ display: 'none' }} 
              />
            </CoverUploadArea>
            
            {/* Username */}
            <FormGroup>
              <Input
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                placeholder="@username"
                required
              />
            </FormGroup>
            
            {/* First Name */}
            <FormGroup>
              <Input
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                required
              />
            </FormGroup>
            
            {/* Last Name */}
            <FormGroup>
              <Input
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                required
              />
            </FormGroup>
            
            {/* Tagline */}
            <FormGroup>
              <Input
                name="tagline"
                type="text"
                value={formData.tagline}
                onChange={handleChange}
                placeholder="Add your tagline"
              />
            </FormGroup>
            
            {/* Bio */}
            <FormGroup>
              <TextArea
                name="bio"
                value={formData.bio}
                onChange={handleChange}
                placeholder="Add your bio"
              />
            </FormGroup>
            
            <SetupButton type="submit" disabled={loading}>
              {loading ? 'Setting up...' : 'Setup Profile'}
            </SetupButton>
          </Form>
        </ProfileCard>
      </ContentSide>
    </Container>
  );
};

export default CompleteProfile;
